import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurant } from "@/hooks/useRestaurant";
import { toast } from "sonner";

export function useTableConsumptionSettings() {
  const { activeRestaurant, refetch } = useRestaurant();
  const [showTableConsumptionValue, setShowTableConsumptionValue] = useState(false);
  const [tableInactivityMinutes, setTableInactivityMinutes] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (activeRestaurant) {
      // Type assertion to access the new fields
      const restaurant = activeRestaurant as typeof activeRestaurant & { 
        show_table_consumption_value?: boolean;
        table_inactivity_minutes?: number;
      };
      setShowTableConsumptionValue(restaurant.show_table_consumption_value ?? false);
      setTableInactivityMinutes(restaurant.table_inactivity_minutes ?? 15);
    }
  }, [activeRestaurant]);

  const updateShowConsumption = useCallback(async (enabled: boolean) => {
    if (!activeRestaurant) return false;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("restaurants")
        .update({ show_table_consumption_value: enabled } as any)
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setShowTableConsumptionValue(enabled);
      await refetch();
      toast.success(enabled 
        ? "Valor de consumo visível nas mesas" 
        : "Valor de consumo ocultado"
      );
      return true;
    } catch (error) {
      console.error("Error updating setting:", error);
      toast.error("Erro ao atualizar configuração");
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [activeRestaurant, refetch]);

  const updateInactivityMinutes = useCallback(async (minutes: number) => {
    if (!activeRestaurant) return false;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("restaurants")
        .update({ table_inactivity_minutes: minutes } as any)
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setTableInactivityMinutes(minutes);
      await refetch();
      toast.success(`Tempo de inatividade: ${minutes} minutos`);
      return true;
    } catch (error) {
      console.error("Error updating setting:", error);
      toast.error("Erro ao atualizar configuração");
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [activeRestaurant, refetch]);

  return {
    showTableConsumptionValue,
    tableInactivityMinutes,
    isLoading,
    isUpdating,
    updateShowConsumption,
    updateInactivityMinutes,
  };
}
